// components
import ButtonV1 from '../../components/ButtonVariants/ButtonV1';

// types
import { DoctorAvailabilityData, DateAvailability } from 'data-types';

// utils
import { formatDateStamp, getTimeDifference, combineDateAndTime } from '../../utils/timestampFormatter';
import { translate } from '../../utils/translator';

// constants
import { TWENTY_FOUR_HOURS_MS } from '../../utils/consts';
import { TIMEZONES } from '../../utils/consts';


type Props = {
	language: string;
    country: string;
	selectedDoctor: DoctorAvailabilityData | null;
	selectedDate: DateAvailability | null;
	selectedTime: {start: string, end: string} | null;
    additionalClassName?: string;
    handleSubmit: () => void;
    allowSubmit: boolean;
    style?: React.CSSProperties;
    layoutWithDoctors: boolean;
}

const AppointmentSummary: React.FC<Props> = ({ language, selectedDoctor, selectedDate, country, selectedTime, additionalClassName, handleSubmit, allowSubmit, style, layoutWithDoctors }) => {
	// Check if appointment is within 24 hours
	const isWithin24Hours = (): boolean => {
		if (!selectedTime || !selectedDate) {
			return false;
		}
		// Extract time portion to ensure we always use current selectedDate.date
		const timePortion = selectedTime.start.includes('T') 
			? selectedTime.start.split('T')[1] 
			: selectedTime.start;
		const fullDateTime = combineDateAndTime(selectedDate.date, timePortion);
		return getTimeDifference(fullDateTime) <= TWENTY_FOUR_HOURS_MS;
	};

	return (
        <div
            id="appointment-summary-container"
            className={`
                bg-[#EFF2EF]
                rounded-[16px]
                border-[2px] lg:border-[3px]
                border-solid
                border-[#ffffff]
                px-5 lg:px-8
                py-4 lg:py-9
                flex
                flex-col
                custom-scrollbar
                md:max-h-[460px]
                ${additionalClassName}
            `}
            style={style}
        >
            <h3 
                id="appointment-summary-title"
                className="
                    leading-[1.3]
                    text-[21px] lg:text-[23px] 
                    font-medium
                    text-[#000000]
                    first-letter:uppercase
                    pb-2
                "
            >
                {translate(language, 'choose_doctor', layoutWithDoctors ? 'appointment_summary/title_with_doctors' : 'appointment_summary/title_without_doctors')}
            </h3>
            
            <ul
                id="appointment-summary-list"
                className="
                    flex
                    flex-col
                    gap-y-[10px]
                    min-h-0
                    flex-1
                "
            >
                {/* 1. doctor's name */}
                <li 
                    className="
                        flex
                        flex-row
                        justify-start
                        items-start
                        text-[16px] lg:text-[18px]
                        gap-2
                    "
                    key="doctor-name"
                >
                            {/* label */}
                            <span
                                id="appointment-summary-doctor-name"
                                className="
                                    text-[#4B5B4B]
                                    font-normal
                                    first-letter:uppercase
                                    font-roboto
                                    whitespace-nowrap
                                "
                            >
                                {translate(language, 'choose_doctor', 'appointment_summary/doctor')}:
                            </span>

                            {/* value */}
                            <span
                                id="appointment-summary-doctor-name-value"
                                className="
                                    text-[#000000]
                                    font-medium
                                    first-letter:uppercase
                                    font-roboto
                                "
                            >
                        {selectedDoctor 
                            ? selectedDoctor.name 
                            : translate(language, 'choose_doctor', 'preferred_time/video_consultation_doctor')
                        }
                            </span>
                </li>

                {/* 2. date */}
                <li 
                    className="
                        flex
                        flex-row
                        justify-start
                        items-start
                        text-[16px] lg:text-[18px]
                        gap-2
                "
                    key="date"
                >
                            {/* label */}
                            <span
                                id="appointment-summary-date"
                                className="
                                    text-[#4B5B4B]
                                    font-normal
                                    first-letter:uppercase
                                    font-roboto
                                    whitespace-nowrap
                                "
                            >
                                {translate(language, 'choose_doctor', 'appointment_summary/date')}:
                            </span>

                    {/* value or placeholder */}
                            <span
                                id="appointment-summary-date-value"
                        className={`
                                    font-medium
                                    first-letter:uppercase
                                    font-roboto
                                    text-[16px] lg:text-[18px]
                            ${selectedDate ? 'text-[#000000]' : 'text-gray-400 italic'}
                        `}
                            >
                        {selectedDate ? formatDateStamp(selectedDate.date, { noDayString: true, showYear: false, locale: `${language}-${country.toUpperCase()}`, timeZone: TIMEZONES[country as keyof typeof TIMEZONES]}) : translate(language, 'choose_doctor', 'preferred_time/select_date') || 'Select date'}
                            </span>
                </li>

                {/* 3. time */}
                <li 
                    className="
                        flex
                        flex-row
                        justify-start
                        items-start
                        text-[16px] lg:text-[18px]
                        gap-2
                    "
                    key="time"
                >
                            {/* label */}
                            <span
                                id="appointment-summary-time"
                                className="
                                    text-[#4B5B4B]
                                    font-normal
                                    first-letter:uppercase
                                    font-roboto
                                    whitespace-nowrap
                                "
                            >
                                {translate(language, 'choose_doctor', 'appointment_summary/time')}:
                            </span>
                            
                    {/* value or placeholder */}
                           	<span
                                id="appointment-summary-time-value"
                        className={`
                                    font-medium
                                    font-roboto
                            ${selectedTime ? 'text-[#000000] uppercase' : 'text-gray-400 italic first-letter:uppercase'}
                        `}
                            >
                        {selectedTime ? (() => {
                            // Extract time portion from datetime string and format to HH:mm
                            const formatTime = (datetimeStr: string): string => {
                                const timePart = datetimeStr.includes('T') 
                                    ? datetimeStr.split('T')[1] 
                                    : datetimeStr;
                                // Remove seconds, keep only HH:mm
                                return timePart.substring(0, 5);
                            };
                            
                            const startTime = formatTime(selectedTime.start);
                            const endTime = formatTime(selectedTime.end);
                            
                            return `${startTime} - ${endTime}`;
                        })() : (translate(language, 'choose_doctor', 'appointment_summary/no_time_selected') || 'Select time range')}
                            </span>
                </li>

                {/* 4. confirm appointment button */}
                <li
                    className="
                        my-5
                        md:mt-auto
                        flex
                        flex-col
                        justify-center
                        md:items-center
                        gap-y-3
                        gap-x-[1em]
                        flex-shrink-0
                    "
                    key="confirm-appointment-button"
                >
                    {/* confirm appointment button */}
                    <ButtonV1
                        disabled={!allowSubmit}
                        className="
                            text-[16px]
                            md:text-[14px]
                            w-full
                            md:w-auto
                            md:max-w-[400px]
                            py-3
                            md:py-2.5
                            px-6
                            md:px-10
                            border-none
                        "
                        onClick={handleSubmit}
                    >
                        {translate(language, 'choose_doctor', 'appointment_summary/confirm_appointment')}
                    </ButtonV1>
                </li>

                {/* 5. short notice disclaimer */}
                {isWithin24Hours() && (
                    <li
                        id="appointment-summary-short-notice-disclaimer"
                        className="
                            flex
                            flex-col
                            justify-start
                            text-[#4B5B4B]
                            italic
                            leading-[1.3]
                            text-[13px] lg:text-[15px]
                            animate-fade-in-up-10
                            flex-shrink-0
                        "
                        key="short-notice-disclaimer"
                    >
                        <p>
                            {translate(language, 'choose_doctor', layoutWithDoctors ? 'appointment_summary/short_notice_disclaimer_with_doctors' : 'appointment_summary/short_notice_disclaimer_without_doctors')}
                        </p>
                    </li>
                )}
            </ul>
        </div>  
	);
}

export default AppointmentSummary;
